(() => {
  'use strict';

  const initialized = new WeakSet();
  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)');

  const money = (cents) => {
    const currency = document.documentElement.dataset.currency || window.Shopify?.currency?.active || 'USD';
    const locale = document.documentElement.lang || undefined;
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format((Number(cents) || 0) / 100);
    } catch (_) {
      return ((Number(cents) || 0) / 100).toFixed(2);
    }
  };

  const variantsFor = (root) => {
    try {
      return JSON.parse(root.querySelector('[data-qx-variant-data]')?.textContent || '[]');
    } catch (_) {
      return [];
    }
  };

  const currentVariant = (root, variants) => {
    const id = root.querySelector('[data-variant-id]')?.value;
    return variants.find((variant) => String(variant.id) === String(id)) || variants[0] || null;
  };

  const syncMedia = (root, variant) => {
    if (!variant?.featured_media_id) return;
    const mediaId = String(variant.featured_media_id);
    const track = root.querySelector('[data-product-media-track]');
    const media = root.querySelector(`[data-media-id="${mediaId}"]`);
    const thumbs = [...root.querySelectorAll('[data-media-thumb]')];
    if (!media) return;

    root.querySelectorAll('[data-media-id]').forEach((item) => item.classList.toggle('is-active', item === media));
    thumbs.forEach((thumb) => {
      const active = String(thumb.dataset.mediaThumb) === mediaId;
      thumb.classList.toggle('is-active', active);
      if (active) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });

    const activeThumb = thumbs.find((thumb) => String(thumb.dataset.mediaThumb) === mediaId);
    activeThumb?.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
      behavior: reduceMotion?.matches ? 'auto' : 'smooth'
    });

    if (track?.scrollTo) {
      track.scrollTo({ left: media.offsetLeft, behavior: reduceMotion?.matches ? 'auto' : 'smooth' });
    }
  };

  const initShare = (root) => {
    if (root.dataset.qxPdpVisualReady === 'true') return;
    root.dataset.qxPdpVisualReady = 'true';
    const share = root.querySelector('[data-qx-pdp-share]');
    share?.addEventListener('click', async () => {
      try {
        const data = { title: document.title, url: location.href };
        if (navigator.share) await navigator.share(data);
        else if (navigator.clipboard) await navigator.clipboard.writeText(location.href);
      } catch (_) {}
    });
  };

  const initQuantity = (root) => {
    if (root.dataset.qxQuantityReady === 'true') return;
    root.dataset.qxQuantityReady = 'true';

    const input = root.querySelector('[data-product-qty] input[name="quantity"], .qx-pdp__form input[name="quantity"]');
    const cards = [...root.querySelectorAll('[data-qx-quantity-choice]')];
    const variantInput = root.querySelector('[data-variant-id]');
    const variants = variantsFor(root);
    if (!input) return;

    const paint = ({ syncVariantMedia = false } = {}) => {
      const qty = Math.max(1, Number.parseInt(input.value || '1', 10) || 1);
      input.value = String(qty);
      const variant = currentVariant(root, variants);

      cards.forEach((card) => {
        const cardQty = Math.max(1, Number.parseInt(card.dataset.qxQuantityChoice || '1', 10) || 1);
        const selected = cardQty === qty;
        card.classList.toggle('is-selected', selected);
        card.setAttribute('aria-pressed', selected ? 'true' : 'false');
        const total = card.querySelector('[data-qx-quantity-total]');
        const unit = card.querySelector('[data-qx-unit-price]');
        if (variant && total) total.textContent = money(Number(variant.price) * cardQty);
        if (variant && unit) unit.textContent = money(variant.price);
      });

      if (!variant) return;

      const stickyPrice = root.querySelector('[data-mobile-price]');
      const desktopPrice = root.querySelector('[data-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = money(Number(variant.price) * qty);
      if (desktopPrice) desktopPrice.textContent = money(Number(variant.price) * qty);

      const mobileVariant = root.querySelector('[data-mobile-variant]');
      const desktopVariant = root.querySelector('[data-sticky-variant]');
      const variantTitle = variant.title === 'Default Title' ? '' : (variant.title || '');
      if (mobileVariant) mobileVariant.textContent = variantTitle;
      if (desktopVariant) desktopVariant.textContent = variantTitle;

      const stickyImage = root.querySelector('[data-sticky-image] img');
      if (stickyImage && variant.featured_media_src) {
        stickyImage.src = variant.featured_media_src;
        stickyImage.removeAttribute('srcset');
      }

      const available = Boolean(variant.available);
      const label = available ? 'Add to cart' : 'Sold out';
      const desktopAdd = root.querySelector('[data-desktop-add]');
      const mobileAdd = root.querySelector('[data-mobile-add]');
      const mainAdd = root.querySelector('[data-product-add]');
      [desktopAdd, mobileAdd, mainAdd].forEach((button) => {
        if (!button) return;
        button.disabled = !available;
      });
      if (desktopAdd) desktopAdd.textContent = label;
      if (mobileAdd) mobileAdd.textContent = label;
      const mainLabel = mainAdd?.querySelector('[data-product-add-text]');
      const mainPrice = mainAdd?.querySelector('[data-product-add-price]');
      if (mainLabel) mainLabel.textContent = label;
      if (mainPrice) mainPrice.textContent = money(Number(variant.price) * qty);

      if (syncVariantMedia) syncMedia(root, variant);
    };

    cards.forEach((card) => card.addEventListener('click', () => {
      input.value = String(Math.max(1, Number.parseInt(card.dataset.qxQuantityChoice || '1', 10) || 1));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      paint();
    }));

    input.addEventListener('input', () => paint());
    input.addEventListener('change', () => paint());
    root.addEventListener('click', (event) => {
      if (event.target.closest('[data-qty-minus],[data-qty-plus]')) requestAnimationFrame(() => paint());
    });

    variantInput?.addEventListener('change', () => paint({ syncVariantMedia: true }));
    paint({ syncVariantMedia: true });
  };

  const initSellingPlans = (root) => {
    if (root.dataset.qxPlansReady === 'true') return;
    root.dataset.qxPlansReady = 'true';

    const box = root.querySelector('[data-qx-selling-plans]');
    if (!box) return;
    const hidden = box.querySelector('[data-qx-selling-plan-input]');
    const variantInput = root.querySelector('[data-variant-id]');
    const variants = variantsFor(root);

    const sync = () => {
      const variant = currentVariant(root, variants);
      if (!variant) return;
      const allocations = variant.selling_plans || [];

      box.querySelectorAll('[data-selling-plan-id]').forEach((label) => {
        const allocation = allocations.find((item) => String(item.id) === String(label.dataset.sellingPlanId));
        label.hidden = !allocation;
        const price = label.querySelector('small');
        if (allocation && price) price.textContent = money(allocation.price);
      });

      const checked = box.querySelector('[data-qx-purchase-option]:checked');
      if (checked && checked.value !== 'one_time' && !allocations.some((item) => String(item.id) === String(checked.value))) {
        const fallback = box.querySelector('[data-qx-purchase-option][value="one_time"]') || box.querySelector('[data-selling-plan-id]:not([hidden]) [data-qx-purchase-option]');
        if (fallback) {
          fallback.checked = true;
          fallback.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    };

    box.addEventListener('change', (event) => {
      const radio = event.target.closest('[data-qx-purchase-option]');
      if (!radio) return;
      box.querySelectorAll('.qx-pdp__purchase-option').forEach((option) => option.classList.toggle('is-selected', option.contains(radio)));
      if (hidden) hidden.value = radio.value === 'one_time' ? '' : radio.value;
    });

    variantInput?.addEventListener('change', sync);
    sync();
  };

  const initRoot = (root) => {
    if (!root || initialized.has(root)) return;
    initialized.add(root);
    initShare(root);
    initQuantity(root);
    initSellingPlans(root);
  };

  const initAll = (scope = document) => {
    if (scope.matches?.('[data-product-root]')) initRoot(scope);
    scope.querySelectorAll?.('[data-product-root]').forEach(initRoot);
  };

  const start = () => {
    initAll();
    const main = document.getElementById('MainContent');
    if ('MutationObserver' in window && main) {
      new MutationObserver((records) => {
        records.forEach((record) => record.addedNodes.forEach((node) => {
          if (node instanceof Element) initAll(node);
        }));
      }).observe(main, { childList: true, subtree: true });
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
